import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from 'antd';
import PropTypes from 'prop-types';

import { getCategories } from './state/costcodes.actions';

import OntraccrTag from '../common/OntraccrTag';

import { LABOR_CATEGORY_ID, OVERHEAD_CATEGORY_ID } from '../helpers/costcodeHelpers';

export default function CostcodeCategorySelector({
  onChange,
  displayMode,
  categoryIds = [],
  useRedesign = false,
}) {
  const dispatch = useDispatch();
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);
  const { paidFlags = [] } = useSelector((state) => state.settings.company ?? {});
  const hasCostTracking = paidFlags.includes('COST_TRACKING');

  useEffect(() => {
    dispatch(getCategories());
  }, []);

  const timeTrackingCategoryIds = useMemo(() => {
    const ids = new Set([LABOR_CATEGORY_ID, OVERHEAD_CATEGORY_ID]);
    Object.values(categoriesIdMap).forEach(({ id, allowTimeTracking }) => {
      if (allowTimeTracking) ids.add(id);
    });
    return ids;
  }, [categoriesIdMap]);

  const selectedTimeTrackingCategoryId = useMemo(() => (
    categoryIds.find((id) => timeTrackingCategoryIds.has(id))
  ), [categoryIds, timeTrackingCategoryIds]);

  const body = useMemo(() => (
    Object.values(categoriesIdMap).map(({ id, name }) => {
      const isTimeTrackingCategory = timeTrackingCategoryIds.has(id);
      const disabled = hasCostTracking
        && isTimeTrackingCategory
        && selectedTimeTrackingCategoryId
        && selectedTimeTrackingCategoryId !== id;

      return (
        <Select.Option key={id} id={id} value={id} disabled={disabled}>
          {name}
        </Select.Option>
      );
    })
  ), [categoriesIdMap, timeTrackingCategoryIds, selectedTimeTrackingCategoryId]);

  const categoryNames = useMemo(() => (
    categoryIds.map((categoryId) => categoriesIdMap[categoryId]?.name || '')
  ), [categoryIds, categoriesIdMap]);

  if (displayMode) {
    return (
      <>
        {categoryNames.map((categoryName) => (
          <OntraccrTag key={categoryName}>{categoryName}</OntraccrTag>
        ))}
      </>
    );
  }

  return (
    <Select
      className={useRedesign ? 'costcode-form-select' : undefined}
      placeholder="Select a Cost Code Category"
      onChange={(val) => onChange(hasCostTracking ? val : [val])}
      value={categoryIds}
      mode={hasCostTracking ? 'multiple' : 'single'}
    >
      {body}
    </Select>
  );
}

/* eslint-disable react/forbid-prop-types */
CostcodeCategorySelector.propTypes = {
  onChange: PropTypes.func,
  displayMode: PropTypes.bool,
  categoryIds: PropTypes.array,
  useRedesign: PropTypes.bool,
};

CostcodeCategorySelector.defaultProps = {
  displayMode: false,
  categoryIds: [],
  onChange: () => {},
  useRedesign: false,
};
